import type {
  PlaybookId,
  PlaybookNode,
} from "@repo/app-types/entities/playbook";
import type { RecordingId } from "@repo/app-types/entities/recording";
import type { RecordingAssessmentFeedback } from "@repo/app-types/entities/recordingAssessmentFeedback";

/**
 * localStorage drafts of AI grading feedback, so text typed into the feedback
 * popover is still there after an outside click closes it. One draft per
 * recording, playbook and criteria node. The key includes the grading type, so
 * a draft answer never comes back after the criterion switches to a different
 * set of answers (e.g. yes/no to a 1-5 score).
 */

export interface GradingAssessmentFeedbackValues {
  correctValue: string | null;
  explanation: string;
}

interface StoredDraft extends GradingAssessmentFeedbackValues {
  /** `updated_at` of the saved feedback the draft was typed over, if any. */
  feedbackUpdatedAt: string | null;
}

export function feedbackDraftStorageKey(
  recordingId: RecordingId,
  playbookId: PlaybookId,
  node: PlaybookNode,
): string {
  const gradingType =
    node.type === "section" ? "section" : node.grading_value_type;
  return `alpharun.gradingFeedbackDraft.${recordingId}.${playbookId}.${node.id}.${gradingType}`;
}

export function savedFeedbackValues(
  feedback: RecordingAssessmentFeedback | undefined,
): GradingAssessmentFeedbackValues {
  return {
    correctValue: feedback?.data.correct_value ?? null,
    explanation: feedback?.data.explanation ?? "",
  };
}

/** Returns the draft unless the saved feedback has changed since it was typed. */
export function readFeedbackDraft(
  storageKey: string,
  feedback: RecordingAssessmentFeedback | undefined,
): GradingAssessmentFeedbackValues | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }
    const draft: Partial<StoredDraft> | null = JSON.parse(raw);
    if (
      typeof draft?.explanation !== "string" ||
      draft.feedbackUpdatedAt !== (feedback?.updated_at ?? null)
    ) {
      return null;
    }
    return {
      correctValue:
        typeof draft.correctValue === "string" ? draft.correctValue : null,
      explanation: draft.explanation,
    };
  } catch {
    // Storage unavailable (e.g. privacy mode) or corrupted content.
    return null;
  }
}

/** Stores the draft, or removes it when it matches the saved feedback. */
export function writeFeedbackDraft(
  storageKey: string,
  values: GradingAssessmentFeedbackValues,
  feedback: RecordingAssessmentFeedback | undefined,
) {
  const savedValues = savedFeedbackValues(feedback);
  try {
    if (
      values.correctValue === savedValues.correctValue &&
      values.explanation === savedValues.explanation
    ) {
      window.localStorage.removeItem(storageKey);
    } else {
      const draft: StoredDraft = {
        ...values,
        feedbackUpdatedAt: feedback?.updated_at ?? null,
      };
      window.localStorage.setItem(storageKey, JSON.stringify(draft));
    }
  } catch {
    // Best-effort: a storage failure only loses the draft.
  }
}

export function removeFeedbackDraft(storageKey: string) {
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // Best-effort: a storage failure only loses the draft.
  }
}
